import { useState, useEffect } from 'react';
import SEO from '@/components/SEO';
import { useParams, Link } from 'react-router-dom';
import { Calendar, ArrowLeft, ArrowRight, TrendingUp } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ContactPopup from '@/components/ContactPopup';
import { Button } from '@/components/ui/button';
import { getNewsArticleBySlug, newsArticles } from '@/data/mediaContent';

const NewsArticle = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  useEffect(() => {
    const foundArticle = getNewsArticleBySlug(slug);
    if (foundArticle) {
      setArticle(foundArticle);
      
      // Get related articles from same type
      const related = newsArticles
        .filter(a => a.type === foundArticle.type && a.id !== foundArticle.id)
        .slice(0, 3);
      
      // If not enough of same type, fill with other recent articles
      if (related.length < 3) {
        const additional = newsArticles
          .filter(a => a.id !== foundArticle.id && !related.includes(a))
          .slice(0, 3 - related.length);
        setRelatedArticles([...related, ...additional]);
      } else {
        setRelatedArticles(related);
      }
    }
  }, [slug]);

  const getImportanceBadge = (importance) => {
    const colors = {
      High: 'bg-primary-100 text-primary-700 border-red-300',
      Medium: 'bg-secondary-100 text-orange-700 border-orange-300',
      Low: 'bg-gray-100 text-gray-700 border-gray-300'
    };
    return colors[importance] || colors.Medium;
  };

  if (!article) {
    return (
      <>
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-4xl font-bold mb-4">Article Not Found</h1>
          <p className="text-secondary-600 mb-8">The news article you're looking for doesn't exist.</p>
          <Link to="/media/news-updates" className="text-primary-600 hover:bg-primary-500 font-semibold">
            ← Back to News & Updates
          </Link>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEO 
        title={article.title}
        description={article.excerpt}
        keywords={`${article.type}, ${article.importance} priority, trademark news, IP updates`}
        ogImage={article.image}
      />
      <Header />
      
      {/* Article Header */}
      <article className="bg-white">
        {/* Breadcrumb */}
        <div className="bg-transparent border-b border-gray-200">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center gap-2 text-sm text-secondary-600">
              <Link to="/" className="hover:text-primary-600">Home</Link>
              <span>/</span>
              <Link to="/media/news-updates" className="hover:text-primary-600">News & Updates</Link>
              <span>/</span>
              <span className="text-gray-900">{article.title}</span>
            </div>
          </div>
        </div>

        {/* Hero Section */}
        <div className="relative h-96 bg-gray-900">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover opacity-80"
          />
          <div className="absolute inset-0 bg-black/60"></div>
          <div className="absolute top-6 left-6">
            <span className={`px-4 py-1.5 rounded-full text-sm font-semibold border ${getImportanceBadge(article.importance)}`}>
              {article.importance} Priority
            </span>
          </div>
        </div>

        {/* Article Content */}
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            {/* Type Badge */}
            <div className="mb-4">
              <span className="bg-primary-600 text-white px-4 py-1.5 rounded-full text-sm font-semibold">
                {article.type}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              {article.title}
            </h1>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-6 text-secondary-600 mb-8 pb-8 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary-600" />
                <span>{article.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary-600" />
                <span className="font-medium">{article.importance} Priority</span>
              </div>
            </div>

            {/* Article Content */}
            <div 
              className="prose prose-lg max-w-none
                prose-headings:font-bold prose-headings:text-gray-900
                prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4
                prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-3
                prose-h4:text-xl prose-h4:mt-6 prose-h4:mb-2
                prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6
                prose-ul:my-6 prose-ul:space-y-2
                prose-ol:my-6 prose-ol:space-y-2
                prose-li:text-gray-700
                prose-strong:text-gray-900 prose-strong:font-semibold
                prose-a:text-primary-600 prose-a:no-underline hover:prose-a:underline
                prose-blockquote:border-l-4 prose-blockquote:border-primary-600 prose-blockquote:pl-6 prose-blockquote:italic
                prose-code:text-primary-600 prose-code:bg-gray-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Newsletter Signup */}
            <div className="mt-12 p-8 bg-primary-600 rounded-2xl text-white">
              <h3 className="text-2xl font-bold mb-3">Stay Updated with Legal News</h3>
              <p className="text-gray-300 mb-6">
                Subscribe to our newsletter for the latest industry updates, legal changes, and expert insights.
              </p>
              <form className="flex flex-col sm:flex-row gap-4">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  className="flex-1 px-6 py-3 rounded-full text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <Button
                  type="submit"
                  className="py-6 bg-secondary-600 text-white px-8 rounded-full font-semibold hover:bg-white hover:text-primary-600 hover:shadow-lg whitespace-nowrap"
                >
                  Subscribe Now
                </Button>
              </form>
            </div>

            {/* Back to News */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <Link 
                to="/media/news-updates"
                className="inline-flex items-center gap-2 text-primary-600 hover:bg-primary-500 font-semibold transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to All News
              </Link>
            </div>
          </div>
        </div>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <div className="bg-transparent py-16">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-3xl font-bold text-gray-900 mb-8">Related News</h2>
                <div className="grid md:grid-cols-3 gap-8">
                  {relatedArticles.map((relatedArticle) => (
                    <Link
                      key={relatedArticle.id}
                      to={`/media/news/${relatedArticle.slug}`}
                      className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 group"
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={relatedArticle.image}
                          alt={relatedArticle.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute top-4 left-4">
                          <span className="bg-primary-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                            {relatedArticle.type}
                          </span>
                        </div>
                      </div>
                      <div className="p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {relatedArticle.title}
                        </h3>
                        <p className="text-secondary-600 text-sm mb-4 line-clamp-3">
                          {relatedArticle.excerpt}
                        </p>
                        <div className="flex items-center text-sm text-gray-500">
                          <Calendar className="w-4 h-4 mr-1" />
                          {relatedArticle.date}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </article>

      <Footer />
      <ContactPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </>
  );
};

export default NewsArticle;